'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "contextualNodeAttributes"],
  _excluded2 = ["disabled", "contextualNodeAttributes"],
  _excluded3 = ["contextualNodeAttributes"],
  _excluded4 = ["contextualNodeAttributes"];
import { BlockAlignmentBottomIcon, BlockAlignmentMiddleIcon, BlockAlignmentTopIcon, FullWidthIcon, ImageAlignmentLeftIcon, ImageAlignmentMiddleIcon, ImageAlignmentRightIcon, InlineIcon } from 'foundations-assets';
import AlignmentDropdown from 'rich-text-lib/components/toolbar/AlignmentDropdown';
import CanvasToolbarButton from 'rich-text-lib/components/toolbar/ToolbarButton';
import { Alignments, VerticalAlignments } from 'rich-text-lib/constants/alignments';
import tinymce from 'tinymce';
import { CANVAS_ALIGNMENT_CENTER, CANVAS_ALIGNMENT_DROPDOWN, CANVAS_ALIGNMENT_FULL_WIDTH, CANVAS_ALIGNMENT_INLINE, CANVAS_ALIGNMENT_LEFT, CANVAS_ALIGNMENT_RIGHT, CANVAS_VERTICAL_ALIGNMENT_BOTTOM, CANVAS_VERTICAL_ALIGNMENT_DROPDOWN, CANVAS_VERTICAL_ALIGNMENT_MIDDLE, CANVAS_VERTICAL_ALIGNMENT_TOP } from '../../../constants/toolbar';
import { contextualI18nKey } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nAlignmentKey = suffix => contextualI18nKey(`alignment.tooltip.${suffix}`);
const i18nVerticalAlignmentKey = suffix => contextualI18nKey(`verticalAlignment.tooltip.${suffix}`);
const ContextualAlignmentControls = [{
  controlName: CANVAS_ALIGNMENT_INLINE,
  alignment: Alignments.NONE,
  iconName: 'inline',
  Icon: InlineIcon,
  tooltip: 'inline'
}, {
  controlName: CANVAS_ALIGNMENT_LEFT,
  alignment: Alignments.LEFT,
  iconName: 'imageAlignmentLeft',
  Icon: ImageAlignmentLeftIcon,
  tooltip: 'left'
}, {
  controlName: CANVAS_ALIGNMENT_CENTER,
  alignment: Alignments.CENTER,
  iconName: 'imageAlignmentMiddle',
  Icon: ImageAlignmentMiddleIcon,
  tooltip: 'center'
}, {
  controlName: CANVAS_ALIGNMENT_RIGHT,
  alignment: Alignments.RIGHT,
  iconName: 'imageAlignmentRight',
  Icon: ImageAlignmentRightIcon,
  tooltip: 'right'
}, {
  controlName: CANVAS_ALIGNMENT_FULL_WIDTH,
  alignment: Alignments.JUSTIFY,
  iconName: 'fullWidth',
  Icon: FullWidthIcon,
  tooltip: 'fullWidth',
  flag: 'useFullWidth'
}];
const ContextualVerticalAlignmentControls = [{
  controlName: CANVAS_VERTICAL_ALIGNMENT_BOTTOM,
  alignment: VerticalAlignments.BOTTOM,
  iconName: 'blockAlignmentBottom',
  Icon: BlockAlignmentBottomIcon,
  tooltip: 'bottom'
}, {
  controlName: CANVAS_VERTICAL_ALIGNMENT_MIDDLE,
  alignment: VerticalAlignments.MIDDLE,
  iconName: 'blockAlignmentMiddle',
  Icon: BlockAlignmentMiddleIcon,
  tooltip: 'middle'
}, {
  controlName: CANVAS_VERTICAL_ALIGNMENT_TOP,
  alignment: VerticalAlignments.TOP,
  iconName: 'blockAlignmentTop',
  Icon: BlockAlignmentTopIcon,
  tooltip: 'top'
}];
export function addContextualAlignmentControls(editor) {
  const getAlignmentStyleButtonClickHandler = (target, alignment, prevAlignment) => () => {
    editor.fire('hsAlignmentChange', {
      target,
      alignment,
      prevAlignment
    });
  };
  ContextualAlignmentControls.forEach(({
    controlName,
    alignment,
    iconName,
    tooltip
  }) => {
    tinymce.canvas.addButton(editor, controlName, {
      renderControl: props => {
        const {
            disabled: _disabled,
            contextualNodeAttributes
          } = props,
          rest = _objectWithoutPropertiesLoose(props, _excluded);
        const {
          alignment: alignmentValue,
          [`${alignment}Disabled`]: disabledAlignment,
          [`${alignment}DisabledTooltip`]: disabledTooltip,
          activeElement
        } = contextualNodeAttributes;
        const disabled = _disabled || disabledAlignment;
        return /*#__PURE__*/_jsx(CanvasToolbarButton, Object.assign({}, rest, {
          "data-test-id": controlName,
          iconName: iconName,
          selected: alignment === alignmentValue,
          disabled: disabled,
          onClick: getAlignmentStyleButtonClickHandler(activeElement, alignment, alignmentValue),
          tooltip: i18nAlignmentKey(tooltip),
          disabledTooltip: disabledTooltip
        }));
      }
    });
  });
  const handleAlignmentChange = (target, prevAlignment, alignment) => {
    editor.fire('hsAlignmentChange', {
      target,
      alignment,
      prevAlignment
    });
  };
  const getAlignmentDropdownOptions = (_disabled, contextualProps) => ContextualAlignmentControls.filter(({
    flag
  }) => flag ? !!contextualProps[flag] : true).map(({
    alignment,
    iconName,
    Icon,
    tooltip: tooltipKey
  }) => {
    const disabled = _disabled || !!contextualProps[`${alignment}Disabled`];
    const tooltip = disabled ? contextualProps[`${alignment}DisabledTooltip`] : i18nAlignmentKey(tooltipKey);
    return {
      icon: iconName,
      Icon,
      value: alignment,
      disabled,
      tooltip
    };
  });

  // Add alignment dropdown button which is used for responsiveness. When the
  // toolbar shrinks the individual buttons will collapse into this dropdown
  tinymce.canvas.addButton(editor, CANVAS_ALIGNMENT_DROPDOWN, {
    renderControl: props => {
      const {
          disabled,
          contextualNodeAttributes
        } = props,
        rest = _objectWithoutPropertiesLoose(props, _excluded2);
      const {
        alignment,
        activeElement
      } = contextualNodeAttributes;
      const alignmentOptions = getAlignmentDropdownOptions(disabled, contextualNodeAttributes);
      return /*#__PURE__*/_jsx(AlignmentDropdown, Object.assign({}, rest, {
        "data-test-id": CANVAS_ALIGNMENT_DROPDOWN,
        dropdownClassName: "mce-alignment-dropdown",
        alignmentOptions: alignmentOptions,
        alignment: alignment,
        onChange: handleAlignmentChange.bind(null, activeElement, alignment),
        tooltip: i18nAlignmentKey('dropdown'),
        disabled: disabled
      }));
    }
  });
}
export function addContextualVerticalAlignmentControls(editor) {
  const getVerticalAlignmentButtonClickHandler = (target, value) => () => {
    editor.fire('hsVerticalAlignmentChange', {
      target,
      alignment: value
    });
  };
  ContextualVerticalAlignmentControls.forEach(({
    controlName,
    alignment,
    iconName,
    tooltip
  }) => {
    tinymce.canvas.addButton(editor, controlName, {
      renderControl: props => {
        const {
            contextualNodeAttributes
          } = props,
          rest = _objectWithoutPropertiesLoose(props, _excluded3);
        const {
          verticalAlignment,
          useCellVerticalAlignment,
          activeElement
        } = contextualNodeAttributes;
        if (useCellVerticalAlignment && alignment === VerticalAlignments.BOTTOM) {
          alignment = 'bottom';
        }
        return /*#__PURE__*/_jsx(CanvasToolbarButton, Object.assign({}, rest, {
          "data-test-id": controlName,
          iconName: iconName,
          tooltip: i18nVerticalAlignmentKey(tooltip),
          selected: alignment === verticalAlignment,
          onClick: getVerticalAlignmentButtonClickHandler(activeElement, alignment)
        }));
      }
    });
  });
  const handleVerticalAlignmentChange = (target, value) => {
    editor.fire('hsVerticalAlignmentChange', {
      target,
      alignment: value
    });
  };
  const getVerticalAlignmentDropdownOptions = ({
    useCellVerticalAlignment
  }) => ContextualVerticalAlignmentControls.map(({
    alignment,
    iconName,
    Icon,
    tooltip
  }) => {
    if (useCellVerticalAlignment && alignment === VerticalAlignments.BOTTOM) {
      alignment = 'bottom';
    }
    return {
      icon: iconName,
      Icon,
      value: alignment,
      tooltip: i18nVerticalAlignmentKey(tooltip)
    };
  });

  // Add alignment dropdown button which is used for responsiveness. When the
  // toolbar shrinks the individual buttons will collapse into this dropdown
  tinymce.canvas.addButton(editor, CANVAS_VERTICAL_ALIGNMENT_DROPDOWN, {
    renderControl: props => {
      const {
          contextualNodeAttributes
        } = props,
        rest = _objectWithoutPropertiesLoose(props, _excluded4);
      const {
        verticalAlignment,
        useCellVerticalAlignment,
        activeElement
      } = contextualNodeAttributes;
      const alignmentOptions = getVerticalAlignmentDropdownOptions({
        useCellVerticalAlignment
      });
      return /*#__PURE__*/_jsx(AlignmentDropdown, Object.assign({}, rest, {
        "data-test-id": CANVAS_VERTICAL_ALIGNMENT_DROPDOWN,
        dropdownClassName: "mce-vertical-alignment-dropdown",
        alignmentOptions: alignmentOptions,
        alignment: verticalAlignment,
        onChange: handleVerticalAlignmentChange.bind(null, activeElement),
        tooltip: i18nVerticalAlignmentKey('dropdown')
      }));
    }
  });
}